<template>
    <div class="form-container">
        <b-row>
            <b-col sm="4">
                <b-form-group>
                    <label for="company">Company</label>
                    <b-form-select id="company" v-if="companiesLoaded"  v-model="filterParams.company_id"
                        :plain="true"
                        :options="companies"
                        :disabled="!isMinimumSuperAdmin">
                    </b-form-select>
                    <div v-if="!companiesLoaded" >
                        <i class="fa fa-spin fa-spinner"></i>
                    </div>
                </b-form-group>
            </b-col>
            <b-col sm="4">
                <b-form-group>
                    <label for="area">Area</label>
                    <b-form-select id="area" v-if="areasLoaded"  v-model="filterParams.area_id"
                        :plain="true"
                        :options="areas"
                        :disabled="!isMinimumCompanyUser">
                    </b-form-select>
                    <div v-if="!areasLoaded" >
                    <i class="fa fa-spin fa-spinner"></i> Waiting for company selection
                    </div>
                </b-form-group>
            </b-col>
            <b-col sm="4">
                <b-form-group>
                    <label for="site">Site</label>
                    <b-form-select id="site" v-if="sitesLoaded"  v-model="filterParams.site_id"
                        :plain="true"
                        :options="sites"
                        :disabled="!isMinimumAreaUser">
                    </b-form-select>
                    <div v-if="!sitesLoaded" >
                    <i class="fa fa-spin fa-spinner"></i> Waiting for area selection
                    </div>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col sm="4">
                <b-form-group>
                    <label for="showDeletedTemplates">Include Deleted</label><br/>
                    <c-switch class="mx-1" id="showDeletedTemplates" name="showDeletedTemplates" color="primary"
                            v-model="showDeletedTemplates" label value="1" uncheckedValue="0" dataOn="yes" dataOff="no"/>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="12" sm="8" md="8">
                <p>
                    <b-button v-on:click="getTemplates" variant="primary">
                        <i class="fa fa-search"></i>&nbsp; Fetch Templates
                    </b-button>
                </p>
            </b-col>
        </b-row>
        <div v-if="templatesLoading" class="form-container">
            <LoadingSpinner />
        </div>
        <div v-if="templatesLoaded && templateSelectionProcess" class="form-container">
            <b-row>
                <b-col sm="12">
                    <b-card>
                        <div slot="header">
                            <strong>Available Templates</strong>
                        </div>
                        <div class="form-container">
                            <b-row>
                                <b-col sm="1">
                                    <div class="mb-3" style="text-align: center;">
                                        <strong>Select</strong>
                                    </div>
                                </b-col>
                                <b-col sm="3">
                                    <div class="mb-3">
                                        <strong>Template</strong>
                                    </div>
                                </b-col>
                                <b-col sm="3">
                                    <div class="mb-3">
                                        <strong>Trade Customer</strong>
                                    </div>
                                </b-col>
                                <b-col sm="3">
                                    <div class="mb-3">
                                        <strong>Post Code</strong>
                                    </div>
                                </b-col>
                                <b-col sm="2">
                                    <div class="mb-3">
                                        <strong>Plate Size</strong>
                                    </div>
                                </b-col>
                            </b-row>
                            <b-row v-for="template in templateItems" v-bind:key="template.id" class="output-row">
                                <b-col sm="1">
                                    <b-button 
                                        variant="outline-primary" 
                                        :pressed.sync="template.itemSelected"
                                        block>
                                        <span v-if="template.itemSelected">Yes</span><span v-else>No</span>
                                    </b-button>
                                </b-col>
                                <b-col sm="3">
                                    <b-input-group>
                                        <b-form-input
                                        v-model="template.template_name"
                                        type="text"
                                        class="form-control"
                                        :id="'template-' + template.id + '-' + template.template_name"
                                        readonly
                                        ></b-form-input>
                                    </b-input-group>
                                </b-col>
                                <b-col sm="3">
                                    <b-input-group>
                                        <b-form-input
                                        v-model="template.supplier_name"
                                        type="text"
                                        class="form-control"
                                        :id="'template-' + template.id + '-' + template.supplier_name"
                                        readonly
                                        ></b-form-input>
                                    </b-input-group>
                                </b-col>
                                <b-col sm="3">
                                    <b-input-group>
                                        <b-form-input
                                        v-model="template.postcode"
                                        type="text"
                                        class="form-control"
                                        :id="'template-' + template.id + '-' + template.postcode"
                                        readonly
                                        ></b-form-input>
                                    </b-input-group>
                                </b-col>
                                <b-col sm="2">
                                    <b-input-group>
                                        <b-form-input
                                        v-model="template.plate_size"
                                        type="text"
                                        class="form-control"
                                        :id="'template-' + template.id + '-' + template.plate_size"
                                        readonly
                                        ></b-form-input>
                                    </b-input-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12">
                    <p>
                        <b-button v-on:click="returnSelectedTemplates" variant="primary">
                            Edit Data &nbsp;<i class="fa fa-arrow-circle-right"></i>
                        </b-button>
                    </p>
                    <b-alert v-if="noTemplateSelectError" variant="danger" show><strong>At least two templates must be selected</strong>
                    </b-alert>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>

import LoadingSpinner from '@/components/loadingSpinner/LoadingSpinner';
import { Switch as cSwitch } from '@coreui/vue';
import cTable from '@/components/table/Table';
import LookupDataService from '@/services/lookup-data-service.js';
import TemplatesService from '@/services/templates-service.js';
import { getCurrentUser } from '@/helpers/utils.js';

export default {
    name: 'TemplateListSelection',
    props: ['current-selected'],
    emits: ['templates-selected'],
    components: {
      LoadingSpinner: LoadingSpinner,
      cSwitch,
      cTable
    },
    data () {
      return {
        companies: [],
        areas: [],
        sites: [],
        templateItems: [],
        companiesLoaded: false,
        areasLoaded: false,
        sitesLoaded: false,
        templatesLoaded: false,
        templatesLoading: false,
        filterParams: {
            company_id: -1,
            area_id: -1,
            site_id: -1,
        },
        userAuthLevel : '',
        showDeletedTemplates: false,
        templateSelectionProcess: false,
        noTemplateSelectError: false
      }
    },
    created () {
        const userDetails = getCurrentUser();
        this.$data.filterParams.company_id = userDetails.company_id;
        this.$data.filterParams.area_id = userDetails.area_id;
        this.$data.filterParams.site_id = userDetails.site_id;
        this.$data.userAuthLevel = userDetails.auth;
        this.getInitialData();
        if (this.currentSelected.length > 0) {
            this.$data.templateItems = this.currentSelected;
            this.$data.templatesLoading = false;
            this.$data.templatesLoaded = true;
            this.$data.templateSelectionProcess = true;
        }
    },
    watch: {
        'filterParams.company_id': function(val, oldVal) {
            
            // get the branches for the selected company
            // clear it out first
            this.$data.areas = [];
            LookupDataService.getAreaList(val)
                .then((areaList) => {
                    if (this.$data.userAuthLevel == 'Super Admin' || this.$data.userAuthLevel == 'Company Users') {
                        this.$data.areas.push({value:0, text: 'All Areas'});
                        this.$data.filterParams.area_id = 0;
                        this.$data.filterParams.site_id = 0;
                    }
                    areaList.map((item) => {
                        this.$data.areas.push(item);
                    });
                    this.$data.areasLoaded = true;
                    //this.$data.filterParams.company_id = val;
                    const userFilter = {
                        company_id: this.$data.filterParams.company_id,
                        area_id: this.$data.filterParams.area_id,
                        site_id: this.$data.filterParams.site_id
                    };
                    
                    // if (oldVal != -1) {
                    //     this.getTemplates();
                    // }
                })
                .catch ((err) => {
                    console.error(err);
                }); 
        },
        'filterParams.area_id': function(val, oldVal) {

            // clear it out first
            this.$data.sites = [];
            this.$data.sites.push({value:0, text: 'All Sites'});
            this.$data.filterParams.site_id = 0;

            if (parseInt(val) > 0) {
                LookupDataService.getSiteList(val)
                    .then((sitesList) => {
                        // if (this.$data.userAuthLevel == 'Super Admin' || this.$data.userAuthLevel == 'Company Users') {
                        //     this.$data.sites.push({value:0, text: 'All Sites'});
                        //     this.$data.filterParams.site_id = 0;
                        // }
                        sitesList.map((item) => {
                            this.$data.sites.push(item);
                        });
                        this.$data.sitesLoaded = true;
                        //this.filterTemplates();
                    })
                    .catch ((err) => {
                        console.error(err);
                    });
            }
        },
        'filterParams.site_id': function(val, oldVal) {

            this.$data.filterParams.site_id = val;
            
        }
    },
    computed: {
        isMinimumSuperAdmin() {
            let returnVal = false;
            switch (this.$data.userAuthLevel) {
                case 'Super Admin':
                case 'Read Only':
                returnVal = true;
                break;
                default:
                returnVal = false;
            }
            
            return returnVal;
        },
        isMinimumCompanyUser() {
            let returnVal = false;
            console.log('Company: ', this.$data.userAuthLevel);
            switch (this.$data.userAuthLevel) {
                case 'Super Admin':
                case 'Company Users':
                case 'Read Only':
                returnVal = true;
                break;
                default:
                returnVal = false;
            }
            console.log('Company: ', returnVal);
            return returnVal;
        },
        isMinimumAreaUser() {
            let returnVal = false;
            console.log('Area: ', this.$data.userAuthLevel);
            switch (this.$data.userAuthLevel) {
                case 'Super Admin':
                case 'Company Users':
                case 'Area Users':
                case 'Read Only':
                returnVal = true;
                break;
                default:
                returnVal = false;
            }
            console.log('Area: ', returnVal);
            return returnVal;
        },
        isMinimumSiteUser() {
            let returnVal = false;
            console.log('Site: ', this.$data.userAuthLevel);
            switch (this.$data.userAuthLevel) {
                case 'Super Admin':
                case 'Company Users':
                case 'Area Users':
                case 'Site Users':
                case 'Read Only':
                returnVal = true;
                break;
                default:
                returnVal = false;
            }
            
            return returnVal;
        }
    },
    methods: {
        async getInitialData() {
            this.$data.companies = await LookupDataService.getCompaniesList();
            this.$data.companiesLoaded = true;
        },
        async getTemplates() {
            this.$data.templatesLoaded = false;
            this.$data.templatesLoading = true;
            const userFilter = {
                company_id: this.$data.filterParams.company_id,
                area_id: this.$data.filterParams.area_id,
                site_id: this.$data.filterParams.site_id,
                is_deleted: this.$data.showDeletedTemplates
            };
            const templatetList = await TemplatesService.getTemplates(userFilter);

            this.$data.templateItems = templatetList.map((templateItem) => {
                templateItem.plate_size = templateItem.plate_width + "x" + templateItem.plate_height;
                templateItem.area_name = templateItem.area.area_name;
                templateItem.supplier_name = templateItem.supplier.supplier_name;
                if (templateItem.postcode == null || templateItem.postcode.length < 1) {
                    templateItem.postcode = '<None>';
                }
                if(templateItem.is_default) templateItem.is_default="1";
                
                templateItem.is_default_text = templateItem.is_default == 0 ? 'No' : 'Yes';
                templateItem.itemSelected = false;
                return templateItem;
            });
            
            this.$data.templatesLoading = false;
            this.$data.templatesLoaded = true;
            this.$data.templateSelectionProcess = true;
        },
        returnSelectedTemplates() {
            const selectedTemplates = this.$data.templateItems.filter((item) => {
                return (item.itemSelected);
            });

            if (selectedTemplates.length < 2) {
                this.$data.noTemplateSelectError = true;
            }
            else {
                this.$data.noTemplateSelectError = false;
                this.$emit('templates-selected', this.$data.templateItems);
            }
        }
    }
}

</script>

<style scoped>
.row.output-row {
    padding-bottom: 8px;
}
</style>